import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type {
  RemoteSpeakerDayDto,
  RemoteSpeakerEnableUiScope,
  RemoteSpeakerScope,
} from '@/types/dtos/contributors/request/configure-remote-speaker.dto';
import { deriveRemoteSpeakerDayIdsForScope } from '@/types/dtos/contributors/request/configure-remote-speaker.dto';
import { useConfigureRemoteSpeakerMutation } from '@/state/mutations/contributors/use-configure-remote-speaker';
import { useNotifications } from '@/hooks/use-notifications';
import { useEventStore } from '@/state/stores/use-event-store';
import { getOriginEventId } from '@/helpers/event.helper';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';
import { useInsufficientRemoteSpeakerDaysInfo } from '@/hooks/use-insufficient-remote-speaker-days-info';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto';
import { useProductsStore } from '@/state/stores/use-products-store';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders';
import { useCreateEventOrderMutation } from '@/state/mutations/orders/use-create-order';
import { useUpdateOrderMutation } from '@/state/mutations/orders/use-update-order';
import { ORDER_STATUS } from '@/types/dtos/orders/response/order.dto';
import { upsertOrderLineItemInPayload } from '@/helpers/order-line-item.helper';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal';
import {
  contributorHasActiveRemoteSpeakerDays,
  clampRemoteSpeakerDayCheckoutQuantity,
} from '@/utils/remote-speaker-status.utils';

export type RemoteSpeakerScopeModalProps = {
  open: boolean;
  onClose: () => void;
  contributor: ContributorDto;
  enabled: boolean;
  flowId: string;
  roleId: string;
  slotId?: string;
  /** When false, only flow (+ event for managers) scopes are offered. */
  showSlotOption?: boolean;
};

function toApiScope(selectedScope: RemoteSpeakerEnableUiScope): RemoteSpeakerScope {
  return selectedScope === 'manual' ? 'event' : selectedScope;
}

export function RemoteSpeakerScopeModal({
  open,
  onClose,
  contributor,
  enabled,
  flowId,
  roleId,
  slotId,
  showSlotOption = false,
}: RemoteSpeakerScopeModalProps) {
  const { t } = useTranslation();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const originEventId = event ? getOriginEventId(event) : undefined;
  const { data: permissions } = usePermissionsQuery();
  const isEventManager =
    permissions?.events?.[originEventId ?? '']?.includes(EVENT_PERMISSIONS.MANAGE_EVENT_ROLES) ?? false;
  const canAddToCheckout = permissions?.events?.[originEventId ?? '']?.includes('create_order') ?? false;

  const isMultiFlow = event?.multiFlow === true;
  const hasActiveRemoteSpeakerDays = contributorHasActiveRemoteSpeakerDays(contributor);

  const availableScopes = useMemo((): RemoteSpeakerEnableUiScope[] => {
    if (!enabled) {
      const scopes: RemoteSpeakerScope[] = [];
      if (showSlotOption && slotId) {
        scopes.push('slot');
      }
      scopes.push('flow');
      if (isEventManager && !hasActiveRemoteSpeakerDays) {
        scopes.push('event');
      }
      return scopes;
    }

    const scopes: RemoteSpeakerEnableUiScope[] = [];
    if (isEventManager && !showSlotOption) {
      scopes.push('manual');
    }
    if (showSlotOption && slotId) {
      scopes.push('slot');
    }
    scopes.push('flow');
    if (isEventManager && isMultiFlow) {
      scopes.push('event');
    }
    return scopes;
  }, [enabled, showSlotOption, slotId, isEventManager, isMultiFlow, hasActiveRemoteSpeakerDays]);

  const [selectedScope, setSelectedScope] = useState<RemoteSpeakerEnableUiScope>('flow');
  const [defaultForNewRoles, setDefaultForNewRoles] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const defaultScope =
        showSlotOption && slotId && availableScopes.includes('slot')
          ? 'slot'
          : availableScopes.includes('flow')
            ? 'flow'
            : (availableScopes[0] ?? 'flow');
      setSelectedScope(defaultScope);
      setDefaultForNewRoles(contributor.remoteSpeakerDefaultForNewRoles ?? false);
    }
  }, [open, availableScopes, contributor.remoteSpeakerDefaultForNewRoles, showSlotOption, slotId]);

  const eventForDays = eventPlan ?? event;
  const scopeDayIds = useMemo(
    () =>
      enabled ? deriveRemoteSpeakerDayIdsForScope(contributor, eventForDays, selectedScope, { roleId, slotId }) : [],
    [enabled, contributor, eventForDays, selectedScope, roleId, slotId],
  );

  const { mutateAsync: configureRemoteSpeaker, isPending } = useConfigureRemoteSpeakerMutation();
  const insufficientInfo = useInsufficientRemoteSpeakerDaysInfo(event, contributor, scopeDayIds);
  const catalogProducts = useProductsStore((s) => s.products);
  const { data: orders } = useGetEventOrdersQuery(originEventId);
  const { mutateAsync: createEventOrder } = useCreateEventOrderMutation();
  const { mutateAsync: updateOrder } = useUpdateOrderMutation();

  const displayName = contributor.profiles[0]?.displayName ?? t('event.detail.contributors.unknownContributor');
  const dayOptions = eventPlan?.days ?? event?.days ?? [];
  const hasMultipleDays = dayOptions.length > 1;
  const needsPurchase = enabled && insufficientInfo !== null;
  const confirmOpensCheckout = needsPurchase && canAddToCheckout;

  const addRemoteSpeakerDaysToCheckout = async () => {
    if (!originEventId || !insufficientInfo || !canAddToCheckout) {
      return false;
    }

    const product = catalogProducts?.find((entry) => entry.key === PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY);
    if (!product) {
      return false;
    }

    const linePayload = {
      productId: product.id,
      quantity: clampRemoteSpeakerDayCheckoutQuantity(insufficientInfo.neededCredits, dayOptions.length, contributor),
      contributorId: contributor.id,
    };
    if (linePayload.quantity < 1) {
      notification.notifyError(t('event.detail.contributors.remoteSpeaker.noRemainingDaysToPurchase'));
      return false;
    }

    const latestOrder = orders?.find(
      (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
    );
    if (latestOrder?.status === ORDER_STATUS.DRAFT) {
      await updateOrder({
        orderId: latestOrder.id,
        lineItems: upsertOrderLineItemInPayload(latestOrder.lineItems, linePayload),
      });
    } else {
      await createEventOrder({ eventId: originEventId, lineItems: [linePayload] });
    }
    setCheckoutOpen(true);
    return true;
  };

  const handleConfirm = async () => {
    if (!originEventId) return;

    const apiScope = toApiScope(selectedScope);

    try {
      let openedCheckout = false;
      const result = await configureRemoteSpeaker({
        eventId: originEventId,
        contributorId: contributor.id,
        scope: apiScope,
        enabled,
        flowId,
        roleId,
        slotId: apiScope === 'slot' ? slotId : undefined,
        applyToExistingContributions: enabled
          ? selectedScope === 'event'
            ? true
            : selectedScope === 'manual'
              ? false
              : undefined
          : apiScope === 'event'
            ? true
            : undefined,
        defaultForNewRoles: enabled && isEventManager ? defaultForNewRoles : undefined,
        dayIds: enabled ? scopeDayIds : [],
        purchaseIfNeeded: enabled && canAddToCheckout,
      });

      if (enabled && result.checkoutRequired && confirmOpensCheckout) {
        openedCheckout = await addRemoteSpeakerDaysToCheckout();
      }

      if (enabled) {
        const hasPending = result.remoteSpeakerDays.some((entry: RemoteSpeakerDayDto) => entry.status === 'pending');
        if (hasPending && !openedCheckout) {
          notification.notifySuccess(
            t('event.detail.contributors.remoteSpeaker.pendingNotification', { name: displayName }),
          );
        } else {
          notification.notifySuccess(t('session.remoteSpeaker.enabledNotification', { name: displayName }));
        }
      } else {
        notification.notifySuccess(t('session.remoteSpeaker.disabledNotification', { name: displayName }));
      }

      onClose();
    } catch {
      notification.notifyError(t('session.remoteSpeaker.toggleError', { name: displayName }));
    }
  };

  const scopeLabelKey = (scope: RemoteSpeakerEnableUiScope) => {
    switch (scope) {
      case 'slot':
        return 'event.detail.contributors.remoteSpeaker.disableScopeSlot' as const;
      case 'flow':
        return 'event.detail.contributors.remoteSpeaker.disableScopeFlow' as const;
      case 'manual':
        return 'event.detail.contributors.remoteSpeaker.enableScopeManual' as const;
      case 'event':
        return 'event.detail.contributors.remoteSpeaker.disableScopeEvent' as const;
    }
  };

  const scopeHintKey = (scope: RemoteSpeakerEnableUiScope) => {
    switch (scope) {
      case 'slot':
        return enabled
          ? ('event.detail.contributors.remoteSpeaker.enableScopeSlotHint' as const)
          : ('event.detail.contributors.remoteSpeaker.disableScopeSlotHint' as const);
      case 'flow':
        return enabled
          ? ('event.detail.contributors.remoteSpeaker.enableScopeFlowHint' as const)
          : ('event.detail.contributors.remoteSpeaker.disableScopeFlowHint' as const);
      case 'manual':
        return 'event.detail.contributors.remoteSpeaker.enableScopeManualHint' as const;
      case 'event':
        return enabled
          ? ('event.detail.contributors.remoteSpeaker.enableScopeEventHint' as const)
          : ('event.detail.contributors.remoteSpeaker.disableScopeEventHint' as const);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>
          {enabled
            ? t('event.detail.contributors.remoteSpeaker.enableTitle', { name: displayName })
            : t('event.detail.contributors.remoteSpeaker.disableScopeTitle', { name: displayName })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {enabled
              ? t('event.detail.contributors.remoteSpeaker.enableScopeDescription', { name: displayName })
              : t('event.detail.contributors.remoteSpeaker.disableScopeDescription', { name: displayName })}
          </DialogContentText>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedScope}
              onChange={(event) => setSelectedScope(event.target.value as RemoteSpeakerEnableUiScope)}
            >
              <Stack spacing={0.5}>
                {availableScopes.map((scope) => (
                  <FormControlLabel
                    key={scope}
                    value={scope}
                    control={<Radio />}
                    label={
                      <Stack spacing={0}>
                        <span>{t(scopeLabelKey(scope))}</span>
                        <DialogContentText component="span" variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                          {t(scopeHintKey(scope), { name: displayName })}
                        </DialogContentText>
                      </Stack>
                    }
                    sx={{ alignItems: 'flex-start', mx: 0 }}
                  />
                ))}
              </Stack>
            </RadioGroup>
            {enabled && isEventManager ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={defaultForNewRoles}
                    onChange={(event) => setDefaultForNewRoles(event.target.checked)}
                  />
                }
                label={t('event.detail.contributors.remoteSpeaker.defaultForNew')}
                sx={{ alignItems: 'center', mx: 0, mt: 0.5 }}
              />
            ) : null}
          </FormControl>
          {enabled && hasMultipleDays && scopeDayIds.length > 0 ? (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Typography variant="subtitle2">{t('event.detail.contributors.remoteSpeaker.daysTitle')}</Typography>
              {dayOptions.map((day, index) => {
                const dayId = day.id;
                const checked = scopeDayIds.includes(dayId);
                if (!checked) return null;
                return (
                  <FormControlLabel
                    key={dayId}
                    control={<Checkbox checked disabled />}
                    label={t('event.detail.contributors.remoteSpeaker.dayLabel', {
                      number: index + 1,
                      defaultValue: `Day ${index + 1}`,
                    })}
                  />
                );
              })}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isPending || availableScopes.length === 0 || (enabled && scopeDayIds.length === 0)}
          >
            {isPending ? (
              <CircularProgress size={20} />
            ) : confirmOpensCheckout ? (
              t('event.detail.contributors.remoteSpeaker.confirmAndAddToCart')
            ) : (
              t('common.confirm')
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <EventCatalogOrderCheckoutModal
        open={checkoutOpen}
        event={eventPlan ?? event ?? undefined}
        onModalClose={() => setCheckoutOpen(false)}
      />
    </>
  );
}

/** @deprecated Use RemoteSpeakerScopeModal */
export type RemoteSpeakerDisableScope = RemoteSpeakerScope;

/** @deprecated Use RemoteSpeakerScopeModal */
export type RemoteSpeakerDisableScopeModalProps = Omit<RemoteSpeakerScopeModalProps, 'enabled'>;

/** @deprecated Use RemoteSpeakerScopeModal */
export function RemoteSpeakerDisableScopeModal(props: RemoteSpeakerDisableScopeModalProps) {
  return <RemoteSpeakerScopeModal {...props} enabled={false} />;
}
